import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseTaxForm8949Options = {
  taxYear: number;
  enabled?: boolean;
};

export type Form8949Event = {
  asset: string;
  quantity: number;
  buyDate: string;
  sellDate: string;
  /** Column (d): net of selling expenses, so (d) - (e) = (h). */
  proceeds: number;
  /** Pre-fee proceeds, for reconciliation only — never column (d). */
  grossProceeds?: number;
  fees?: number;
  cost: number;
  gain: number;
  codeAdjustment: string;
  amountAdjustment: number;
  /**
   * False for rows the jurisdiction exempts (US gifts, Germany §23). They
   * still list on the form's pages but are out of `totals` — display the
   * server's totals rather than re-reducing these events. Legacy rows send
   * nothing, which means taxable.
   */
  is_taxable?: boolean;
};

export type Form8949Totals = {
  /** Net of fees, matching column (d) on the rows. */
  totalProceeds: number;
  totalGrossProceeds?: number;
  totalFees?: number;
  totalCost: number;
  totalGain: number;
  report?: any;
};

/**
 * The year's Schedule D annual figures, authored by the tax engine and stored
 * per (user, year, accounting method). Every surface RENDERS these — nothing on
 * the client nets terms, applies the §1211(b) cap or derives a carryover.
 *
 * Losses are positive in `allowedLoss`, `carryin*` and `carryover*`. Schedule D
 * line 7 is `netShortTerm - carryinShort` and line 15 is
 * `netLongTerm - carryinLong`: the nets here are this year's realized figures
 * BEFORE the carry-in, so the two lines are derived at render rather than sent
 * twice.
 */
export type Form8949Annual = {
  netShortTerm: number;
  netLongTerm: number;
  /** Schedule D lines 6 and 14 — last year's worksheet lines 8 and 13. */
  carryinShort: number;
  carryinLong: number;
  /** Schedule D line 16 (§1222(11)). */
  netCapital: number;
  /** Schedule D line 21, positive. */
  allowedLoss: number;
  /** The §1211(b) cap that applied: 3000, or 1500 for married filing separately. */
  lossLimit: number;
  /** Next year's lines 6 and 14 — §1212(b), each keeping its character. */
  carryoverShort: number;
  carryoverLong: number;
  /**
   * `legacy` means the year was calculated before the engine owned these
   * figures: the netting and the cap are right, but no carryover is known.
   * Surfaces say so rather than printing a carryover of zero as fact.
   */
  source: "stored" | "legacy";
};

/**
 * A year with nothing in it. `stored`, not `legacy`: a screen with no payload
 * at all has nothing to fall back to, and printing "recalculate to see your
 * carryovers" at a taxpayer who has imported no trades would be noise.
 */
export const EMPTY_FORM_8949_ANNUAL: Form8949Annual = {
  netShortTerm: 0,
  netLongTerm: 0,
  carryinShort: 0,
  carryinLong: 0,
  netCapital: 0,
  allowedLoss: 0,
  lossLimit: 0,
  carryoverShort: 0,
  carryoverLong: 0,
  source: "stored",
};

export type Form8949Response = {
  taxYear: number;
  taxSetting: any;
  taxpayer: {
    name?: string;
  };
  annual: Form8949Annual;
  totals: {
    /** Crypto ordinary income for the year — Form 1040 line 8, via Schedule 1. */
    ordinaryIncome: number;
    longTerm: Form8949Totals;
    shortTerm: Form8949Totals;
  };
  events: {
    longTerm: Form8949Event[];
    shortTerm: Form8949Event[];
  };
};

export const useTaxForm8949 = ({
  taxYear,
  enabled = true,
}: UseTaxForm8949Options): UseQueryResult<Form8949Response | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchForm8949 = async (): Promise<Form8949Response | null> => {
    const token = await getToken();
    const response = await authorizedAxios(token).get(
      "/export/tax-report/us/form8949",
      {
        params: { year: taxYear },
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 204,
      }
    );

    if (response.status === 204) {
      return null;
    }

    return response.data;
  };

  return useQuery({
    queryKey: ["tax_form_8949", taxYear],
    queryFn: fetchForm8949,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
